/**
 * Configures how URLs are constructed in the application.
 */
import {InitialRequestUrl, RequestInfo} from "@khan/shared-core";
import {
    configureUrlConstructionRules,
    UrlRules,
    type UrlRule,
} from "@khan/urls";

const doNotExposeClassCodeOnLogout: UrlRule = (url) => {
    const continuePath = url.queryParams.get("continue");

    // For security reasons, we don't want to expose the class code of a
    // user upon logout.
    if (
        url.basePath.includes("/logout") &&
        continuePath?.startsWith("/teacher/class")
    ) {
        url.queryParams.delete("continue");
    }
    return url;
};

/**
 * Add query params to the URL if we're embedded in the browser extesion.
 *
 * This is only necessary for apps that support being embedded in the browser
 * extension. It is needed to support logging-in through the browser extension.
 */
const supportExtension: UrlRule = (url) => {
    // If we're logging in through the browser extension, we need to
    // maintain some URL params on the /login page. These are needed so
    // that webapp->extension messaging will work.
    if (RequestInfo.isExtension) {
        url.queryParams.set("extension", "1");

        const originalUrl = new URL(InitialRequestUrl.href);
        const originalParams = originalUrl.searchParams;
        const tabId = originalParams.get("tabId");
        if (tabId) {
            url.queryParams.set("tabId", tabId);
        }
    }
    return url;
};

/**
 * A map of path IDs to paths.
 */
export const paths = Object.freeze({
    signup: "/signup",
    login: "/login",
    logout: "/logout",
    account: "/settings/account",
    forgotPassword: "/forgotpw",
    privacyPolicy: "/about/privacy-policy",
    termsOfService: "/about/docs/khan-academy-terms-of-service",
});

/**
 * Setup URL construction rules for the application.
 */
export const setupUrlConstruction = () =>
    configureUrlConstructionRules(
        (id) => paths[id],
        [
            UrlRules.initializeContinueParam(),
            UrlRules.mapParam("continue", (v) =>
                v?.startsWith("/sat") ? "/mission/sat" : v,
            ),
            UrlRules.noContinueForAuthUrls("continue"),
            doNotExposeClassCodeOnLogout,
            supportExtension,
        ],
    );
